import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Wallet, TrendingUp, TrendingDown, DollarSign, BarChart3, PieChart,
  ArrowUpRight, ArrowDownRight, RefreshCw, Target, Award, AlertTriangle,
  Briefcase, Activity, Clock, Percent, ChevronRight, LineChart,
  CircleDollarSign, Coins, Building2, BarChart2, Zap
} from 'lucide-react';

import { API } from '@/config/api';

// Color palette for charts
const CHART_COLORS = [
  '#D4AF37', '#10B981', '#3B82F6', '#8B5CF6', '#F59E0B',
  '#EF4444', '#EC4899', '#06B6D4', '#84CC16', '#F97316'
];

export default function PortfolioAnalytics() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/portfolio/analytics`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      } else {
        toast.error('Failed to load analytics');
      }
    } catch (e) {
      console.error('Analytics fetch error:', e);
      toast.error('Failed to load analytics');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const formatCurrency = (v) => {
    if (v === undefined || v === null) return '$0.00';
    return `$${parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercent = (v) => {
    if (v === undefined || v === null) return '0.00%';
    return `${v >= 0 ? '+' : ''}${parseFloat(v).toFixed(2)}%`;
  };

  const formatNumber = (v) => {
    if (v === undefined || v === null) return '0';
    return parseFloat(v).toLocaleString('en-US');
  };

  // Calculate allocation percentages for visualization
  const getAllocationData = () => {
    if (!analytics?.asset_allocation?.length) return [];
    
    const total = analytics.asset_allocation.reduce((sum, a) => sum + a.value, 0);
    if (total === 0) return [];
    
    return analytics.asset_allocation
      .filter(a => a.value > 0)
      .map((a, i) => ({
        ...a,
        percent: (a.value / total) * 100,
        color: CHART_COLORS[i % CHART_COLORS.length]
      }))
      .sort((a, b) => b.value - a.value);
  };

  const allocationData = getAllocationData();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading portfolio analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white pt-16" data-testid="portfolio-analytics-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#14120f] via-[#1c1814] to-[#14120f] border-b border-[#1c1814]">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#D4AF37]/20 rounded-lg">
                <PieChart className="h-8 w-8 text-[#D4AF37]" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Portfolio Analytics</h1>
                <p className="text-gray-400">Track your wealth-building journey</p>
              </div>
            </div>
            <Button onClick={fetchAnalytics} variant="outline" className="border-[#D4AF37]/50 text-[#D4AF37]">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-[#14120f] to-[#1c1814] border-[#D4AF37]/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Total Portfolio</span>
                <Wallet className="h-5 w-5 text-[#D4AF37]" />
              </div>
              <p className="text-2xl font-bold text-[#D4AF37]">
                {formatCurrency(analytics?.overview?.total_portfolio_value)}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#14120f] border-[#1c1814]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Cash Balance</span>
                <DollarSign className="h-5 w-5 text-emerald-400" />
              </div>
              <p className="text-2xl font-bold text-white">
                {formatCurrency(analytics?.overview?.cash_balance)}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#14120f] border-[#1c1814]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Invested</span>
                <Briefcase className="h-5 w-5 text-blue-400" />
              </div>
              <p className="text-2xl font-bold text-white">
                {formatCurrency(analytics?.overview?.invested_value)}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#14120f] border-[#1c1814]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Total P&L</span>
                {analytics?.overview?.total_pnl >= 0 ? 
                  <TrendingUp className="h-5 w-5 text-emerald-400" /> : 
                  <TrendingDown className="h-5 w-5 text-red-400" />
                }
              </div>
              <p className={`text-2xl font-bold ${analytics?.overview?.total_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {analytics?.overview?.total_pnl >= 0 ? '+' : ''}{formatCurrency(analytics?.overview?.total_pnl)}
              </p>
              <p className={`text-sm ${analytics?.overview?.total_pnl_percent >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {formatPercent(analytics?.overview?.total_pnl_percent)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#14120f] border border-[#1c1814]">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <BarChart3 className="h-4 w-4 mr-2" /> Overview
            </TabsTrigger>
            <TabsTrigger value="positions" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Briefcase className="h-4 w-4 mr-2" /> Positions
            </TabsTrigger>
            <TabsTrigger value="performance" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Target className="h-4 w-4 mr-2" /> Performance
            </TabsTrigger>
            <TabsTrigger value="activity" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Activity className="h-4 w-4 mr-2" /> Activity
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Asset Allocation */}
              <Card className="bg-[#14120f] border-[#1c1814]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <PieChart className="h-5 w-5 text-[#D4AF37]" />
                    Asset Allocation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {allocationData.length > 0 ? (
                    <div className="space-y-4">
                      {/* Visual bar */}
                      <div className="h-6 rounded-full overflow-hidden flex">
                        {allocationData.map((asset, i) => (
                          <div
                            key={asset.symbol}
                            className="h-full transition-all"
                            style={{ 
                              width: `${asset.percent}%`, 
                              backgroundColor: asset.color,
                              minWidth: asset.percent > 0 ? '2px' : '0'
                            }}
                            title={`${asset.symbol}: ${asset.percent.toFixed(1)}%`}
                          />
                        ))}
                      </div>

                      {/* Legend */}
                      <div className="grid grid-cols-2 gap-2">
                        {allocationData.slice(0, 8).map((asset) => (
                          <div key={asset.symbol} className="flex items-center gap-2 py-1">
                            <div className="w-3 h-3 rounded" style={{ backgroundColor: asset.color }} />
                            <span className="text-sm text-gray-400 flex-1">{asset.symbol}</span>
                            <span className="text-sm text-white font-mono">{asset.percent.toFixed(1)}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <PieChart className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-400">No assets in portfolio</p>
                      <Link to="/trading">
                        <Button size="sm" className="mt-4 bg-[#D4AF37] text-black">
                          Start Trading
                        </Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Account Sources */}
              <Card className="bg-[#14120f] border-[#1c1814]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Wallet className="h-5 w-5 text-[#D4AF37]" />
                    Account Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Alpaca Account */}
                  <div className="p-4 bg-[#0a0a0f] rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Zap className="h-5 w-5 text-[#D4AF37]" />
                        <span className="font-medium text-white">Alpaca Brokerage</span>
                      </div>
                      {analytics?.alpaca?.connected ? (
                        <Badge className={analytics.alpaca.paper ? 'bg-orange-500/20 text-orange-400' : 'bg-emerald-500/20 text-emerald-400'}>
                          {analytics.alpaca.paper ? 'Paper' : 'Live'}
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-500/20 text-gray-400">Not Connected</Badge>
                      )}
                    </div>
                    {analytics?.alpaca?.connected && !analytics.alpaca.error ? (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Portfolio Value</p>
                          <p className="text-lg font-bold text-white">{formatCurrency(analytics.alpaca.portfolio_value)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Buying Power</p>
                          <p className="text-lg font-bold text-emerald-400">{formatCurrency(analytics.alpaca.buying_power)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Positions</p>
                          <p className="text-lg font-bold text-white">{analytics.alpaca.position_count}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Unrealized P&L</p>
                          <p className={`text-lg font-bold ${analytics.alpaca.unrealized_pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {analytics.alpaca.unrealized_pl >= 0 ? '+' : ''}{formatCurrency(analytics.alpaca.unrealized_pl)}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <Link to="/trading">
                        <Button size="sm" variant="outline" className="border-[#D4AF37]/50 text-[#D4AF37]">
                          Connect Alpaca
                        </Button>
                      </Link>
                    )}
                  </div>

                  {/* Paper Trading */}
                  <div className="p-4 bg-[#0a0a0f] rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Coins className="h-5 w-5 text-blue-400" />
                        <span className="font-medium text-white">Paper Trading</span>
                      </div>
                      <Badge className="bg-blue-500/20 text-blue-400">Virtual</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Cash Balance</p>
                        <p className="text-lg font-bold text-white">{formatCurrency(analytics?.paper_trading?.cash_balance)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Crypto Value</p>
                        <p className="text-lg font-bold text-blue-400">{formatCurrency(analytics?.paper_trading?.crypto_value)}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Positions Tab */}
          <TabsContent value="positions" className="space-y-6">
            {/* Alpaca Positions */}
            {analytics?.alpaca?.positions?.length > 0 && (
              <Card className="bg-[#14120f] border-[#1c1814]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <BarChart2 className="h-5 w-5 text-[#D4AF37]" />
                    Stock Positions (Alpaca)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[#0a0a0f]">
                        <tr className="text-gray-500">
                          <th className="px-4 py-3 text-left">Symbol</th>
                          <th className="px-4 py-3 text-right">Shares</th>
                          <th className="px-4 py-3 text-right">Avg Cost</th>
                          <th className="px-4 py-3 text-right">Current</th>
                          <th className="px-4 py-3 text-right">Market Value</th>
                          <th className="px-4 py-3 text-right">P&L</th>
                          <th className="px-4 py-3 text-right">P&L %</th>
                          <th className="px-4 py-3 text-right">Allocation</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.alpaca.positions.map((pos, i) => (
                          <tr key={i} className="border-t border-[#1c1814] hover:bg-[#0a0a0f]">
                            <td className="px-4 py-3 font-bold text-white">{pos.symbol}</td>
                            <td className="px-4 py-3 text-right text-gray-300 font-mono">{formatNumber(pos.shares)}</td>
                            <td className="px-4 py-3 text-right text-gray-400 font-mono">{formatCurrency(pos.avg_price)}</td>
                            <td className="px-4 py-3 text-right text-white font-mono">{formatCurrency(pos.current_price)}</td>
                            <td className="px-4 py-3 text-right text-[#D4AF37] font-mono">{formatCurrency(pos.market_value)}</td>
                            <td className={`px-4 py-3 text-right font-mono ${pos.unrealized_pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {pos.unrealized_pl >= 0 ? '+' : ''}{formatCurrency(pos.unrealized_pl)}
                            </td>
                            <td className={`px-4 py-3 text-right font-mono ${pos.unrealized_plpc >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {formatPercent(pos.unrealized_plpc)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-400">{pos.allocation?.toFixed(1)}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Paper Crypto Holdings */}
            {analytics?.paper_trading?.holdings?.length > 0 && (
              <Card className="bg-[#14120f] border-[#1c1814]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Coins className="h-5 w-5 text-blue-400" />
                    Crypto Holdings (Paper)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[#0a0a0f]">
                        <tr className="text-gray-500">
                          <th className="px-4 py-3 text-left">Asset</th>
                          <th className="px-4 py-3 text-right">Balance</th>
                          <th className="px-4 py-3 text-right">Price</th>
                          <th className="px-4 py-3 text-right">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.paper_trading.holdings.map((holding, i) => (
                          <tr key={i} className="border-t border-[#1c1814] hover:bg-[#0a0a0f]">
                            <td className="px-4 py-3 font-bold text-white">{holding.symbol}</td>
                            <td className="px-4 py-3 text-right text-gray-300 font-mono">{formatNumber(holding.balance)}</td>
                            <td className="px-4 py-3 text-right text-gray-400 font-mono">{formatCurrency(holding.price)}</td>
                            <td className="px-4 py-3 text-right text-blue-400 font-mono">{formatCurrency(holding.value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* No positions */}
            {!analytics?.alpaca?.positions?.length && !analytics?.paper_trading?.holdings?.length && (
              <Card className="bg-[#14120f] border-[#1c1814]">
                <CardContent className="p-12 text-center">
                  <Briefcase className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">No Positions Yet</h3>
                  <p className="text-gray-400 mb-4">Start trading to build your portfolio</p>
                  <Link to="/trading">
                    <Button className="bg-[#D4AF37] text-black">
                      Go to Trading
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance" className="space-y-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-[#14120f] border-[#1c1814]">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-sm">Win Rate</span>
                    <Target className="h-5 w-5 text-emerald-400" />
                  </div>
                  <p className={`text-2xl font-bold ${(analytics?.performance_metrics?.win_rate || 0) >= 50 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {analytics?.performance_metrics?.win_rate || 0}%
                  </p>
                  <Progress 
                    value={analytics?.performance_metrics?.win_rate || 0} 
                    className="mt-2 h-2"
                  />
                </CardContent>
              </Card>

              <Card className="bg-[#14120f] border-[#1c1814]">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-sm">Total Realized P&L</span>
                    <DollarSign className="h-5 w-5 text-[#D4AF37]" />
                  </div>
                  <p className={`text-2xl font-bold ${(analytics?.performance_metrics?.total_realized_pnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {(analytics?.performance_metrics?.total_realized_pnl || 0) >= 0 ? '+' : ''}
                    {formatCurrency(analytics?.performance_metrics?.total_realized_pnl)}
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-[#14120f] border-[#1c1814]">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-sm">Avg Win</span>
                    <ArrowUpRight className="h-5 w-5 text-emerald-400" />
                  </div>
                  <p className="text-2xl font-bold text-emerald-400">
                    +{formatCurrency(analytics?.performance_metrics?.avg_win)}
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-[#14120f] border-[#1c1814]">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-sm">Avg Loss</span>
                    <ArrowDownRight className="h-5 w-5 text-red-400" />
                  </div>
                  <p className="text-2xl font-bold text-red-400">
                    {formatCurrency(analytics?.performance_metrics?.avg_loss)}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Trade Statistics */}
            <Card className="bg-[#14120f] border-[#1c1814]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-[#D4AF37]" />
                  Trade Statistics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="p-4 bg-[#0a0a0f] rounded-lg text-center">
                    <p className="text-gray-500 text-sm mb-1">Total Trades</p>
                    <p className="text-3xl font-bold text-white">{analytics?.trade_history?.total_trades || 0}</p>
                  </div>
                  <div className="p-4 bg-[#0a0a0f] rounded-lg text-center">
                    <p className="text-gray-500 text-sm mb-1">Winners</p>
                    <p className="text-3xl font-bold text-emerald-400">{analytics?.performance_metrics?.winning_trades || 0}</p>
                  </div>
                  <div className="p-4 bg-[#0a0a0f] rounded-lg text-center">
                    <p className="text-gray-500 text-sm mb-1">Losers</p>
                    <p className="text-3xl font-bold text-red-400">{analytics?.performance_metrics?.losing_trades || 0}</p>
                  </div>
                  <div className="p-4 bg-[#0a0a0f] rounded-lg text-center">
                    <p className="text-gray-500 text-sm mb-1">Journaled</p>
                    <p className="text-3xl font-bold text-blue-400">{analytics?.performance_metrics?.journaled_trades || 0}</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Award className="h-5 w-5 text-emerald-400" />
                      <span className="text-emerald-400 font-medium">Best Trade</span>
                    </div>
                    <p className="text-2xl font-bold text-emerald-400">
                      +{formatCurrency(analytics?.performance_metrics?.best_trade)}
                    </p>
                  </div>
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-5 w-5 text-red-400" />
                      <span className="text-red-400 font-medium">Worst Trade</span>
                    </div>
                    <p className="text-2xl font-bold text-red-400">
                      {formatCurrency(analytics?.performance_metrics?.worst_trade)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity" className="space-y-6">
            <Card className="bg-[#14120f] border-[#1c1814]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Activity className="h-5 w-5 text-[#D4AF37]" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                {analytics?.recent_activity?.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.recent_activity.map((activity, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-[#0a0a0f] rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${activity.action === 'buy' ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                            {activity.action === 'buy' ? 
                              <TrendingUp className="h-5 w-5 text-emerald-400" /> : 
                              <TrendingDown className="h-5 w-5 text-red-400" />
                            }
                          </div>
                          <div>
                            <p className="text-white font-medium">
                              {activity.action?.toUpperCase()} {activity.symbol}
                            </p>
                            <p className="text-xs text-gray-500">
                              {activity.amount} units • {activity.is_paper ? 'Paper' : 'Live'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-mono">{formatCurrency(activity.value)}</p>
                          <p className="text-xs text-gray-500">
                            {activity.timestamp ? new Date(activity.timestamp).toLocaleDateString() : '-'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Activity className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">No recent activity</p>
                    <Link to="/trading">
                      <Button size="sm" className="mt-4 bg-[#D4AF37] text-black">
                        Start Trading
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Trades Table */}
            {analytics?.trade_history?.recent_trades?.length > 0 && (
              <Card className="bg-[#14120f] border-[#1c1814]">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Clock className="h-5 w-5 text-[#D4AF37]" />
                      Trade History
                    </CardTitle>
                    <Link to="/trade-journal">
                      <Button size="sm" variant="outline" className="border-[#D4AF37]/50 text-[#D4AF37]">
                        View Journal <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[#0a0a0f]">
                        <tr className="text-gray-500">
                          <th className="px-4 py-3 text-left">Symbol</th>
                          <th className="px-4 py-3 text-left">Type</th>
                          <th className="px-4 py-3 text-right">Qty</th>
                          <th className="px-4 py-3 text-right">Price</th>
                          <th className="px-4 py-3 text-right">Total</th>
                          <th className="px-4 py-3 text-center">Mode</th>
                          <th className="px-4 py-3 text-right">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.trade_history.recent_trades.map((trade, i) => (
                          <tr key={i} className="border-t border-[#1c1814] hover:bg-[#0a0a0f]">
                            <td className="px-4 py-3 font-bold text-white">{trade.symbol}</td>
                            <td className={`px-4 py-3 font-medium ${trade.type === 'buy' ? 'text-emerald-400' : 'text-red-400'}`}>
                              {trade.type?.toUpperCase()}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-300 font-mono">{trade.quantity}</td>
                            <td className="px-4 py-3 text-right text-gray-400 font-mono">{formatCurrency(trade.price)}</td>
                            <td className="px-4 py-3 text-right text-white font-mono">{formatCurrency(trade.total_value)}</td>
                            <td className="px-4 py-3 text-center">
                              <Badge className={trade.is_paper ? 'bg-blue-500/20 text-blue-400' : 'bg-emerald-500/20 text-emerald-400'}>
                                {trade.is_paper ? 'Paper' : 'Live'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-right text-gray-500 text-xs">
                              {trade.created_at ? new Date(trade.created_at).toLocaleDateString() : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
